/**
 * STYLING: Plain Tailwind ONLY!
 * Shows one or more generated variants. Tabs appear only for N>1.
 * Copy always targets the active variant.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy } from 'lucide-react';
import { EID } from '@/lib/element-ids.registry';
import type { TextGenerateResponse } from './textGenerationApi';

interface Props {
  toolCode: string;
  variants: TextGenerateResponse[];
  active: number;
  onActiveChange: (index: number) => void;
}

export function TextToolVariantSwitcher({ toolCode, variants, active, onActiveChange }: Props) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const current = variants[active] ?? variants[0];
  const multi = variants.length > 1;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(current.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      id={multi ? EID['text-tool-variant-switcher-region'] : undefined}
      className="text-tool-variant-switcher space-y-3"
    >
      <div className="flex items-center justify-between gap-3">
        {multi ? (
          <div role="tablist" aria-label={t('textTools.variants.label')} className="flex flex-wrap gap-1.5">
            {variants.map((_, i) => (
              <button
                key={i}
                role="tab"
                type="button"
                aria-selected={i === active}
                onClick={() => onActiveChange(i)}
                className={`px-3 py-1 text-xs rounded-full border ${
                  i === active
                    ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-950/40 dark:text-primary-300'
                    : 'border-neutral-300 dark:border-neutral-600 text-neutral-600 dark:text-neutral-400'
                }`}
              >
                {t('textTools.variants.tab', { n: i + 1 })}
              </button>
            ))}
          </div>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={handleCopy}
          className="flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-200 shrink-0"
        >
          {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
          {copied ? t('textTools.copied') : t('textTools.copy')}
        </button>
      </div>
      <div
        id={`text-tool-result-content-${toolCode}`}
        className="bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 text-sm whitespace-pre-wrap text-neutral-900 dark:text-neutral-100"
      >
        {current.content}
      </div>
    </div>
  );
}
